/**
 * DDI portfolio page: the slide, and beneath it the two short texts per row
 * that the DDI audience reads instead of the full status narrative.
 *
 * One page. The slide sits on top as large as the window allows, the rows
 * beneath it grouped by portfolio, each with its two texts. "Read the report"
 * on a row opens that initiative's status report as readable text right
 * under its boxes, so reading and writing happen in the same place. Clicking
 * a row on the slide scrolls to it below.
 *
 * The portfolio pills narrow the whole page, slide included: pick Finance and
 * the slide is Finance's own, with its numbers worked out for Finance alone,
 * ready to download or present.
 *
 * The month starts blank. Once the status reports are in, someone drafts the
 * whole slide with AI (or starts blank), and from then on the texts save as
 * people type. The AI draft is saved straight away: it is the starting slate,
 * not a proposal waiting for sign-off.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Maximize2, X, AlertTriangle, Lock, Unlock, Sparkles, Loader2, Check, CalendarDays, Download, PenLine,
} from 'lucide-react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import { useAuth } from '../hooks/useAuth'
import { closedMessage, ddiDeadlineChip, fmtUk, resolveDdiWindow, type DdiDeadlineTone } from '../lib/reportingWindow'
import LoadingSpinner from '../components/LoadingSpinner'
import DdiSlide from '../components/DdiSlide'
import ReportSlide, { SLIDE_W, SLIDE_H } from '../components/ReportSlide'
import { FullScreenSlide, useSlidePng } from '../components/SlideExport'
import DdiRowEditor, { type SaveState, type Texts } from '../components/DdiRowEditor'
import { buildDdiRows, ddiGroups, ddiLayout } from '../lib/ddi'
import { currentReportMonth, monthLabel, shiftMonth } from '../lib/report'
import type { DdiDraftEvent, MonthReview, Project, ProjectReport, Settings } from '../types'

// How long after the last keystroke a row saves itself.
const AUTOSAVE_MS = 900

const omit = <T,>(map: Record<string, T>, key: string): Record<string, T> =>
  Object.fromEntries(Object.entries(map).filter(([k]) => k !== key))

// The portfolio a person last narrowed the page to, remembered per browser:
// the leadership team each write and present their own, month after month.
const PORTFOLIO_KEY = 'trackit.ddi.portfolio'
const readPortfolio = () => { try { return localStorage.getItem(PORTFOLIO_KEY) } catch { return null } }
const writePortfolio = (name: string) => { try { localStorage.setItem(PORTFOLIO_KEY, name) } catch { /* private mode */ } }

const CHIP_TONE: Record<DdiDeadlineTone, string> = {
  open: 'bg-[#00ACED]/10 text-[#0077A8] border-[#00ACED]/30',
  overdue: 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
  waiting: 'bg-slate-100 text-slate-500 border-slate-200',
}

const STEP_LABEL: Record<DdiDraftEvent['stage'], string> = {
  gather: 'Reading the status reports',
  draft: 'Writing',
  check: 'Checking facts and wording',
  save: 'Saving to the slide',
}

export default function DdiPortfolio() {
  useDocumentTitle('DDI Portfolio')
  const api = useApi()
  const [params, setParams] = useSearchParams()
  const month = params.get('month') || currentReportMonth()
  const prevMonth = shiftMonth(month, -1)
  // Which row has its report open, in the URL so a link to it can be shared
  // and a refresh lands back on it.
  const openProject = params.get('project')
  // Which portfolio the page is narrowed to: 'all', or one portfolio's rows.
  const chosenPortfolio = params.get('portfolio') ?? readPortfolio() ?? 'all'
  const go = (patch: { month?: string; project?: string | null; portfolio?: string }) =>
    setParams(p => {
      const next = new URLSearchParams(p)
      if (patch.month !== undefined) { next.set('month', patch.month); next.delete('project') }
      if (patch.project !== undefined) {
        if (patch.project) next.set('project', patch.project)
        else next.delete('project')
      }
      if (patch.portfolio !== undefined) {
        next.set('portfolio', patch.portfolio)
        writePortfolio(patch.portfolio)
      }
      return next
    })

  const { data, loading } = useData(
    () =>
      Promise.all([
        api.getProjects(),
        api.getProjectReports(month),
        api.getProjectReports(prevMonth),
        api.getMonthReview(month),
        api.getSettings(),
      ]).then(([projects, reports, prevReports, review, settings]) => ({
        projects: projects as Project[],
        reports: reports as ProjectReport[],
        prevReports: prevReports as ProjectReport[],
        review: review as MonthReview,
        // The slide groups initiatives under the portfolio leading them.
        portfolios: (settings as Settings).portfolios ?? [],
      })),
    [month],
  )

  // This month's reports as last saved, and the texts as typed. The slide
  // previews the typed texts, which reach the server a moment later.
  const [reports, setReports] = useState<Record<string, ProjectReport>>({})
  const [texts, setTexts] = useState<Record<string, Texts>>({})
  const [saves, setSaves] = useState<Record<string, SaveState>>({})
  // Check flags from a fresh per-row draft, which replace the stored ones.
  const [draftIssues, setDraftIssues] = useState<Record<string, string[]>>({})
  const [review, setReview] = useState<MonthReview | null>(null)
  const [fullSize, setFullSize] = useState(false)
  // One initiative's own status report slide, over the page.
  const [slideFor, setSlideFor] = useState<string | null>(null)
  // The slide at native size, which is what the PNG is taken from.
  const { ref: slideRef, download, exporting, error: exportError } = useSlidePng()
  const [ddiAction, setDdiAction] = useState<'close' | 'reopen' | null>(null)
  const [ddiError, setDdiError] = useState('')
  // Starting blank is a choice for this visit only: until someone writes a
  // word, the month still offers the AI draft next time.
  const [startedBlank, setStartedBlank] = useState(false)
  const [drafting, setDrafting] = useState<DdiDraftEvent | { stage: 'gather'; status: 'running' } | null>(null)
  const [draftError, setDraftError] = useState('')
  const [notDrafted, setNotDrafted] = useState<string[]>([])
  // The draft in progress, so it can be stopped; and whether it was.
  const draftAbort = useRef<AbortController | null>(null)
  const [stoppedDraft, setStoppedDraft] = useState(false)
  const [redrafting, setRedrafting] = useState<string | null>(null)
  const [redraftError, setRedraftError] = useState('')

  useEffect(() => {
    if (!data) return
    setReports(Object.fromEntries(data.reports.map(r => [r.projectId, r])))
    setTexts({})
    setSaves({})
    setDraftIssues({})
    setReview(data.review)
    setDdiError('')
    setStartedBlank(false)
    setDrafting(null)
    setDraftError('')
    setNotDrafted([])
    setStoppedDraft(false)
    setRedraftError('')
    setSlideFor(null)
  }, [data])

  // The slide fits the width it is given, never past native.
  const [previewW, setPreviewW] = useState(SLIDE_W)
  const observerRef = useRef<ResizeObserver | null>(null)
  const previewRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node) return
    const measure = (w: number) => { if (w > 0) setPreviewW(w) }
    measure(node.getBoundingClientRect().width)
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(([entry]) => measure(entry.contentRect.width))
      : null
    ro?.observe(node)
    observerRef.current = ro
  }, [])
  useEffect(() => () => observerRef.current?.disconnect(), [])

  // A row picked on the slide is scrolled to once it is open below. Only a
  // slide click scrolls; opening a row from its own heading leaves the page
  // where it is.
  const scrollTo = useRef<string | null>(null)
  useEffect(() => {
    if (!openProject || scrollTo.current !== openProject) return
    scrollTo.current = null
    document.querySelector(`[data-row-editor="${CSS.escape(openProject)}"]`)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [openProject])

  const projects = data?.projects ?? []
  const prevReports = data?.prevReports ?? []
  const portfolios = useMemo(() => data?.portfolios ?? [], [data])
  const portfolioOrder = useMemo(() => portfolios.map(p => p.name), [portfolios])
  // Whether every summary and way forward fits on the slide, even at the
  // smallest type size. The slide never cuts text, so this is the warning.
  const [textFits, setTextFits] = useState(true)

  const rows = useMemo(() => {
    const merged = Object.values(reports).map(r => {
      const typed = texts[r.projectId]
      return typed ? { ...r, ddiSummary: typed.summary, ddiNextSteps: typed.nextSteps } : r
    })
    return buildDdiRows(projects, merged, prevReports, portfolios)
  }, [projects, reports, texts, prevReports, portfolios])
  // Grouped as the slide draws them, which is also the order of the rows below.
  const groups = useMemo(() => ddiGroups(rows, portfolioOrder), [rows, portfolioOrder])

  // --- Autosave -------------------------------------------------------------
  // Each row saves itself shortly after typing stops, and on blur. Refs hold
  // the latest texts so a timer never saves a stale copy, and a row typed into
  // while it saves goes round again once the save lands.
  const textsRef = useRef(texts)
  textsRef.current = texts
  // Pending saves, each remembering the month it was typed in.
  const timers = useRef<Record<string, { timer: ReturnType<typeof setTimeout>; month: string }>>({})
  const inFlight = useRef<Record<string, boolean>>({})
  const again = useRef<Record<string, boolean>>({})
  const monthRef = useRef(month)
  monthRef.current = month

  const saveRow = useCallback(async (projectId: string, forMonth: string) => {
    clearTimeout(timers.current[projectId]?.timer)
    delete timers.current[projectId]
    const typed = textsRef.current[projectId]
    if (!typed) return
    if (inFlight.current[projectId]) {
      again.current[projectId] = true
      return
    }
    inFlight.current[projectId] = true
    setSaves(s => ({ ...s, [projectId]: { state: 'saving' } }))
    try {
      const updated = await api.setProjectReportDdi(forMonth, projectId, {
        ddiSummary: typed.summary,
        ddiNextSteps: typed.nextSteps,
      }) as ProjectReport
      if (monthRef.current !== forMonth) return
      setReports(r => ({ ...r, [projectId]: updated }))
      setSaves(s => ({ ...s, [projectId]: { state: 'saved' } }))
    } catch (e) {
      if (monthRef.current !== forMonth) return
      setSaves(s => ({ ...s, [projectId]: { state: 'error', message: e instanceof Error ? e.message : "Couldn't save." } }))
    } finally {
      inFlight.current[projectId] = false
      if (again.current[projectId]) {
        again.current[projectId] = false
        if (monthRef.current === forMonth) void saveRow(projectId, forMonth)
      }
    }
  }, [api])

  const flushAll = useCallback(() => {
    for (const [id, { month: m }] of Object.entries(timers.current)) void saveRow(id, m)
  }, [saveRow])

  // Leaving the month or the page saves whatever is still waiting.
  useEffect(() => () => flushAll(), [month, flushAll])
  const pending = Object.values(saves).some(s => s.state === 'saving') || Object.keys(timers.current).length > 0
  useEffect(() => {
    if (!pending) return
    const warn = (e: BeforeUnloadEvent) => { flushAll(); e.preventDefault() }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [pending, flushAll])

  // --- Window --------------------------------------------------------------
  // A month with a saved window has its DDI texts closed by hand by a status
  // reporting admin; any other month closes them with the reports on the
  // default rule (see lib/reportingWindow).
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin
  const isReportingAdmin = !!user?.isReportingAdmin
  // Until the effect catches up with a newly loaded month, the fetched review stands in.
  const currentReview = review?.id === month ? review : data?.review
  const ddiWindow = resolveDdiWindow(month, currentReview)
  // Texts closed by hand are closed for everyone, admins included, and Reopen
  // is the way back in. Platform admins stay exempt only on a month with no
  // saved window, which closes on the default rule and has no Reopen.
  const closedByHand = ddiWindow.source === 'manual' && ddiWindow.closed && !ddiWindow.notYetOpen
  const adminExempt = isAdmin && ddiWindow.source !== 'manual'
  const editable = ddiWindow.isOpen || adminExempt
  const chip = ddiDeadlineChip(ddiWindow)

  if (loading || !data) return <LoadingSpinner />

  const filed = rows.filter(r => r.hasReport).length
  const started = !!currentReview?.ddiDraftedAt || startedBlank
    || rows.some(r => r.summary.trim() || r.nextSteps.trim())
  // A month nobody can edit is shown as it stands, blank or not.
  const showStart = rows.length > 0 && editable && !started
  const showSlide = rows.length > 0 && !showStart

  const textsFor = (projectId: string): Texts => texts[projectId] ?? {
    summary: reports[projectId]?.ddiSummary ?? '',
    nextSteps: reports[projectId]?.ddiNextSteps ?? '',
  }
  const issuesFor = (projectId: string): string[] =>
    draftIssues[projectId] ?? (texts[projectId] ? [] : reports[projectId]?.ddiAiIssues ?? [])
  const isWritten = (projectId: string) => {
    const t = textsFor(projectId)
    return !!t.summary.trim() && !!t.nextSteps.trim()
  }

  /** `byHand` is false only for the AI's own redraft, which writes the texts
   *  and the flags together. Anyone else typing is a person taking the row on,
   *  and the flags go, exactly as the server drops them on a human save. */
  const edit = (projectId: string, patch: Partial<Texts>, byHand = true) => {
    setTexts(t => ({ ...t, [projectId]: { ...textsFor(projectId), ...patch } }))
    setSaves(s => ({ ...s, [projectId]: { state: 'saving' } }))
    if (byHand) setDraftIssues(d => (projectId in d ? omit(d, projectId) : d))
    clearTimeout(timers.current[projectId]?.timer)
    timers.current[projectId] = { month, timer: setTimeout(() => void saveRow(projectId, month), AUTOSAVE_MS) }
  }

  const prevTextFor = (projectId: string): Texts | null => {
    const prev = prevReports.find(r => r.projectId === projectId)
    if (!prev?.ddiSummary && !prev?.ddiNextSteps) return null
    return { summary: prev.ddiSummary ?? '', nextSteps: prev.ddiNextSteps ?? '' }
  }

  // The portfolio the page is narrowed to. A link to a row in another
  // portfolio still shows that row: the open row wins over the choice.
  const openRow = rows.find(r => r.projectId === openProject)
  const portfolio = openRow && chosenPortfolio !== 'all' && openRow.portfolio !== chosenPortfolio
    ? openRow.portfolio
    : groups.some(g => g.portfolio === chosenPortfolio) ? chosenPortfolio : 'all'
  const scoped = portfolio === 'all' ? groups : groups.filter(g => g.portfolio === portfolio)
  // The slide shows what the rows show, so a portfolio's own slide can be
  // downloaded or presented on its own.
  const slideRows = portfolio === 'all' ? rows : rows.filter(r => r.portfolio === portfolio)
  const slideScope = portfolio === 'all' ? undefined : portfolio
  const layout = ddiLayout(slideRows.length)

  // A row picked on the slide opens below and is scrolled to. The slide only
  // ever shows the portfolio already chosen, so the pills stay as they are.
  const openFromSlide = (projectId: string) => {
    scrollTo.current = projectId
    go({ project: projectId })
  }

  const draftSlide = async () => {
    const abort = new AbortController()
    draftAbort.current = abort
    setDrafting({ stage: 'gather', status: 'running' })
    setDraftError('')
    setStoppedDraft(false)
    try {
      const result = await api.draftDdiSlide(month, setDrafting, abort.signal)
      const fresh = await api.getProjectReports(month) as ProjectReport[]
      setReports(Object.fromEntries(fresh.map(r => [r.projectId, r])))
      setTexts({})
      setReview(result.review)
      setNotDrafted(result.failed)
    } catch (e) {
      if (abort.signal.aborted) {
        // Stopped by hand. The server keeps what it had saved, so show that.
        const [fresh, currentReview] = await Promise.all([api.getProjectReports(month), api.getMonthReview(month)])
        setReports(Object.fromEntries((fresh as ProjectReport[]).map(r => [r.projectId, r])))
        setTexts({})
        setReview(currentReview as MonthReview)
        setStoppedDraft(true)
      } else {
        setDraftError(e instanceof Error ? e.message : "Couldn't draft the slide.")
      }
    } finally {
      draftAbort.current = null
      setDrafting(null)
    }
  }

  const redraft = async (projectId: string) => {
    setRedrafting(projectId)
    setRedraftError('')
    try {
      const drafted = await api.redraftProjectReportDdi(month, projectId)
      setDraftIssues(d => ({ ...d, [projectId]: drafted.issues }))
      edit(projectId, { summary: drafted.ddiSummary, nextSteps: drafted.ddiNextSteps }, false)
    } catch (e) {
      setRedraftError(e instanceof Error ? e.message : "Couldn't draft this row.")
    } finally {
      setRedrafting(null)
    }
  }

  const setDdiClosed = async (action: 'close' | 'reopen') => {
    setDdiAction(action)
    setDdiError('')
    try {
      setReview(action === 'close' ? await api.closeDdiTexts(month) : await api.reopenDdiTexts(month))
    } catch (e) {
      setDdiError(e instanceof Error ? e.message : `Couldn't ${action} the DDI texts.`)
    } finally {
      setDdiAction(null)
    }
  }

  const drafted = currentReview?.ddiDraftedAt
  const closedBy = ddiWindow.source === 'manual' && ddiWindow.closesAt
    ? `Closed by ${ddiWindow.closedBy?.name || ddiWindow.closedBy?.email || 'a status reporting admin'}, ${fmtUk(ddiWindow.closesAt)}`
    : null
  // The slide fills the width it is given, never past native. Full screen is
  // the way to see it larger.
  const scale = Math.min(1, previewW / SLIDE_W)
  const slideReport = slideFor ? reports[slideFor] : undefined

  const pill = (name: string, label: string, count: number) => (
    <button key={name} type="button" role="tab" aria-selected={portfolio === name}
            onClick={() => go({ portfolio: name, project: null })}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              portfolio === name ? 'bg-[#002677] text-white border-[#002677]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
      {label} <span className="opacity-60 tabular-nums">{count}</span>
    </button>
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-2 flex-wrap">
        <h1 className="text-2xl font-bold text-slate-800">DDI Portfolio</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${CHIP_TONE[chip.tone]}`}>
            {chip.tone === 'closed' ? <Lock className="w-3.5 h-3.5" /> : <CalendarDays className="w-3.5 h-3.5" />}
            {chip.label}
          </span>
          <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => go({ month: prevMonth })} aria-label="Previous month"
                    className="px-2.5 py-2 text-slate-500 hover:bg-slate-50 text-sm">‹</button>
            <div className="px-3 py-2 text-sm font-semibold text-slate-700 min-w-[130px] text-center">
              {monthLabel(month)}
            </div>
            <button onClick={() => go({ month: shiftMonth(month, 1) })} aria-label="Next month"
                    className="px-2.5 py-2 text-slate-500 hover:bg-slate-50 text-sm">›</button>
          </div>
          {ddiWindow.source === 'manual' && isReportingAdmin && (
            <button onClick={() => setDdiClosed(ddiWindow.closed ? 'reopen' : 'close')} disabled={!!ddiAction}
                    className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm disabled:opacity-40">
              {ddiWindow.closed
                ? <><Unlock className="w-4 h-4" /> {ddiAction ? 'Reopening…' : 'Reopen'}</>
                : <><Lock className="w-4 h-4" /> {ddiAction ? 'Closing…' : 'Close DDI texts'}</>}
            </button>
          )}
          {showSlide && (
            <>
              <button onClick={() => download(['UKI Active Portfolio Overview', slideScope, monthLabel(month)].filter(Boolean).join(' '))}
                      disabled={exporting}
                      className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm disabled:opacity-60">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PNG
              </button>
              <button onClick={() => setFullSize(true)}
                      className="bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 shadow-sm">
                <Maximize2 className="w-4 h-4" /> Full screen
              </button>
            </>
          )}
        </div>
      </div>

      {/* One quiet line of context */}
      <div className="mb-4 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
        {drafted && (
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-[#00ACED]" />
            Drafted with AI by {currentReview?.ddiDraftedBy?.name || currentReview?.ddiDraftedBy?.email || 'someone'}, {fmtUk(new Date(drafted))}
          </span>
        )}
        {closedBy && <span>{closedBy}</span>}
        {showSlide && <SaveStatus editable={editable} saves={Object.values(saves)} />}
        {ddiError && <span role="alert" className="text-[#B8242E] font-semibold">{ddiError}</span>}
        {exportError && <span role="alert" className="text-[#B8242E] font-semibold">{exportError}</span>}
        {redraftError && <span role="alert" className="text-[#B8242E] font-semibold">{redraftError}</span>}
      </div>

      {!ddiWindow.isOpen && (
        <Notice tone="amber" icon={Lock}>
          {closedByHand && isReportingAdmin
            ? 'The DDI texts are closed, so the slide is read only. Reopen them to make changes.'
            : closedMessage(month, ddiWindow)}
          {adminExempt && ' As a platform admin you can still edit.'}
        </Notice>
      )}
      {stoppedDraft && (
        <Notice tone="amber" onClose={() => setStoppedDraft(false)}>
          Drafting was stopped. Rows the AI had already saved are on the slide; write the rest by hand, or start again.
        </Notice>
      )}
      {notDrafted.length > 0 && (
        <Notice tone="amber" onClose={() => setNotDrafted([])}>
          There wasn&apos;t enough in these status reports to draft from, so write them by hand: {notDrafted.join(', ')}.
        </Notice>
      )}

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500">
          No projects are flagged for the DDI portfolio yet. Turn one on from the Reporting card on a project page.
        </div>
      ) : showStart ? (
        <StartCard
          month={month}
          filed={filed}
          total={rows.length}
          canDraft={isReportingAdmin}
          drafting={drafting}
          error={draftError}
          onDraft={draftSlide}
          onCancel={() => draftAbort.current?.abort()}
          onBlank={() => setStartedBlank(true)}
        />
      ) : (
        <>
          {groups.length > 1 && (
            <div className="mb-3 flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Portfolio">
              <span className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mr-1">Portfolio</span>
              {pill('all', 'All', rows.length)}
              {groups.map(g => pill(g.portfolio, g.portfolio, g.rows.length))}
            </div>
          )}
          {layout.hidden > 0 && (
            <Notice tone="amber">
              {layout.hidden} row{layout.hidden === 1 ? '' : 's'} will not fit on the slide. Remove some projects from
              the DDI portfolio on their project page.
            </Notice>
          )}
          {!textFits && (
            <Notice tone="amber">
              The texts are too long to fit on the slide, even at the smallest size. Shorten the longest ones below.
            </Notice>
          )}

          {/* The slide */}
          <div ref={previewRef} className="w-full">
            {/* Square-cornered: the slide is a 16:9 rectangle, and a rounded
                frame around it clips its own corners */}
            <div className="shadow-sm border border-slate-200 bg-white overflow-hidden">
              <div style={{ width: SLIDE_W * scale, height: SLIDE_H * scale }}>
                <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                  <div ref={slideRef} style={{ width: SLIDE_W, height: SLIDE_H }}>
                    <DdiSlide rows={slideRows} month={month} scope={slideScope} portfolioOrder={portfolioOrder}
                              onFitChange={setTextFits} onRowClick={openFromSlide} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The rows beneath it */}
          <p className="mt-6 text-xs text-slate-400">Click an initiative on the slide, or its name below, to read its status report.</p>

          <div className="mt-3 space-y-5">
            {scoped.map(g => {
              const withReport = g.rows.filter(r => r.hasReport)
              const written = withReport.filter(r => isWritten(r.projectId)).length
              return (
                <section key={g.portfolio} aria-label={g.portfolio}>
                  <div className="flex items-baseline justify-between gap-3 px-1 mb-1.5">
                    <h2 className="text-sm font-bold text-slate-700">{g.portfolio}</h2>
                    <span className="text-xs text-slate-400 tabular-nums">
                      {withReport.length ? `${written} of ${withReport.length} written` : 'No status reports yet'}
                    </span>
                  </div>
                  <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                    {g.rows.map(row => (
                      <DdiRowEditor
                        key={row.projectId}
                        row={row}
                        report={reports[row.projectId]}
                        month={month}
                        prevMonth={prevMonth}
                        texts={textsFor(row.projectId)}
                        save={saves[row.projectId]}
                        issues={issuesFor(row.projectId)}
                        carried={prevTextFor(row.projectId)}
                        editable={editable}
                        open={openProject === row.projectId}
                        redrafting={redrafting === row.projectId}
                        onToggle={() => go({ project: openProject === row.projectId ? null : row.projectId })}
                        onEdit={patch => edit(row.projectId, patch)}
                        onBlur={() => { if (timers.current[row.projectId]) void saveRow(row.projectId, month) }}
                        onRetry={() => void saveRow(row.projectId, month)}
                        onRedraft={() => void redraft(row.projectId)}
                        onViewSlide={() => setSlideFor(row.projectId)}
                      />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>
        </>
      )}

      {fullSize && (
        <FullScreenSlide onClose={() => setFullSize(false)}>
          <DdiSlide rows={slideRows} month={month} scope={slideScope} portfolioOrder={portfolioOrder} onFitChange={setTextFits} />
        </FullScreenSlide>
      )}
      {slideReport && (
        <FullScreenSlide onClose={() => setSlideFor(null)}>
          <ReportSlide report={slideReport} />
        </FullScreenSlide>
      )}
    </div>
  )
}

function Notice({ tone, children, onClose, icon: Icon = AlertTriangle }: {
  tone: 'amber'
  children: React.ReactNode
  onClose?: () => void
  icon?: typeof AlertTriangle
}) {
  return (
    <div className={`mb-4 rounded-xl border px-4 py-2.5 text-sm flex items-start gap-2.5 ${
      tone === 'amber' ? 'border-amber-200 bg-amber-50 text-slate-700' : ''}`}>
      <Icon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1">{children}</div>
      {onClose && (
        <button onClick={onClose} aria-label="Dismiss" className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

/** One status for the page, so nobody has to hunt for a Save button:
 *  saving as soon as anyone types, saved once every row has landed, and a
 *  plain warning when a row did not. */
function SaveStatus({ editable, saves }: { editable: boolean; saves: SaveState[] }) {
  const base = 'inline-flex items-center gap-1.5 font-semibold'
  if (!editable) {
    return <span className={`${base} text-slate-500`}><Lock className="w-3.5 h-3.5" /> Read only</span>
  }
  const failed = saves.filter(s => s.state === 'error').length
  if (failed) {
    return (
      <span role="alert" className={`${base} text-[#B8242E]`}>
        <AlertTriangle className="w-3.5 h-3.5" /> {failed === 1 ? "A row couldn't save" : `${failed} rows couldn't save`}
      </span>
    )
  }
  if (saves.some(s => s.state === 'saving')) {
    return <span role="status" className={`${base} text-slate-500`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</span>
  }
  return (
    <span role="status" className={`${base} text-[#00843D]`}>
      <Check className="w-3.5 h-3.5" /> {saves.length ? 'All changes saved' : 'Changes save automatically'}
    </span>
  )
}

/** The blank month: one way in, drafting with AI, and a way round it.
 *  Drafting the whole slide is a status reporting admin's job, so everyone
 *  else is offered the writing straight away. */
function StartCard({ month, filed, total, canDraft, drafting, error, onDraft, onCancel, onBlank }: {
  month: string
  filed: number
  total: number
  canDraft: boolean
  drafting: DdiDraftEvent | null
  error: string
  onDraft: () => void
  onCancel: () => void
  onBlank: () => void
}) {
  const label = monthLabel(month)

  if (drafting) {
    const writing = drafting.stage === 'draft' && drafting.total
    const progress = writing ? (drafting.done ?? 0) / drafting.total!
      : ({ gather: 0.05, draft: 0.1, check: 0.9, save: 0.95 } as const)[drafting.stage]
    return (
      <div className="bg-white rounded-2xl border border-slate-200 px-6 py-14 text-center" role="status" aria-live="polite">
        <Loader2 className="w-7 h-7 text-[#00ACED] animate-spin mx-auto" />
        <div className="mt-3 text-base font-bold text-slate-800">
          {writing ? `${STEP_LABEL.draft} ${drafting.done} of ${drafting.total}` : STEP_LABEL[drafting.stage]}
        </div>
        <div className="mt-1 text-sm text-slate-500 h-5">{writing && drafting.row ? drafting.row : ''}</div>
        <div className="mt-5 mx-auto max-w-xs h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#00ACED] transition-all duration-500" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
        <div className="mt-4 text-xs text-slate-400 flex items-center justify-center gap-3 flex-wrap">
          {(['gather', 'draft', 'check', 'save'] as const).map((stage, i, all) => {
            const at = all.indexOf(drafting.stage)
            return (
              <span key={stage} className={`inline-flex items-center gap-1 ${i === at ? 'text-slate-600 font-semibold' : ''}`}>
                {i < at && <Check className="w-3 h-3 text-[#005EB8]" />}
                {({ gather: 'Read', draft: 'Write', check: 'Check', save: 'Save' } as const)[stage]}
              </span>
            )
          })}
        </div>
        <button onClick={onCancel} className="mt-5 text-xs text-slate-500 hover:text-[#B8242E] underline underline-offset-2">
          Stop drafting
        </button>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 px-6 py-14 text-center">
      <div className="w-12 h-12 rounded-full bg-[#00ACED]/10 flex items-center justify-center mx-auto">
        <Sparkles className="w-6 h-6 text-[#005EB8]" />
      </div>
      <div className="mt-4 text-lg font-bold text-slate-800">Start the {label} slide</div>
      {filed === 0 ? (
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
          No status reports are in yet for the {total} DDI projects. The slide is drafted from them, so come back once
          they&apos;re filed.
        </p>
      ) : (
        <>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
            {filed} of {total} status reports are in.{' '}
            {!canDraft
              ? 'A status reporting admin drafts the slide with AI to start the month off, or you can write your rows now.'
              : filed < total
                ? `AI drafts a summary and way forward for those ${filed}; write the other ${total - filed} by hand once their reports are filed.`
                : 'AI drafts a summary and way forward for every row, and you check each one against its report.'}
          </p>
          {canDraft && (
            <>
              <button onClick={onDraft}
                      className="mt-6 bg-[#005EB8] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#004a94] inline-flex items-center gap-2 shadow-sm">
                <Sparkles className="w-4 h-4" /> {error ? 'Try again' : 'Draft with AI'}
              </button>
              {error && <div role="alert" className="mt-3 text-sm text-[#B8242E] font-semibold">{error}</div>}
            </>
          )}
        </>
      )}
      {filed > 0 && (
        canDraft ? (
          <div className="mt-4">
            <button onClick={onBlank} className="text-xs text-slate-500 hover:text-[#005EB8] underline underline-offset-2">
              or start blank
            </button>
          </div>
        ) : (
          <button onClick={onBlank}
                  className="mt-6 bg-[#005EB8] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#004a94] inline-flex items-center gap-2 shadow-sm">
            <PenLine className="w-4 h-4" /> Start writing
          </button>
        )
      )}
    </div>
  )
}
